import { PageTabed } from '../../components-common/layout';
import { ConfigSection } from '../../components-fabric';
import { assignmentEntityConfig } from './entity';
import { PageTable } from '@components-common/layouts';
import { IndexContainer } from '../../components-common/containers/IndexContainer';
import { layoutWorkspace } from '../../_website/_common/layout.workspace';
import { AssignmentSubmitForm } from '../../components-domain/education-domain/assignments/AssignmentSubmitForm';
import { CourseColumn } from './_customCollumns';
import { JsonViewer } from '../../components-domain/generation-domain/specifications';
import { DiStellarViewer } from '../../areas/distellar/DiStellarViewer';
import { downloadFile } from '../../components-common/_shared/actionButton.helper';
import { GroupColumn } from '../../_website/courses/_customCollumns';

/**
 * Configuration Array: assignmentTabs
 *
 * This array contains the configuration for the assignment tabs.
 * Each tab has an id, title, and components to be rendered.
 */
export const assignmentTabs = [
  {
    id: 'assignment_students',
    title: 'Submit',
    components: [
      {
        Component: AssignmentSubmitForm,
        props: {
          empty: {
            buildingConfigurationId: '',
            contents: '',
            fileName: '',
          },
        },
      },
    ],
  },

  {
    id: 'assignment_submissions',
    title: 'Submissions',
    components: [
      {
        Component: IndexContainer,
        props: {
          toolbar: {
            id: 'table_submissions',
            cat: 'toolbar',
            type: 'index',
            left: [],
          },
          table: {
            cat: 'table',
            type: 'editor',
            id: 'table_submissions',
            props: {
              source: {
                type: 'REMOTE',
                url: '/coursesms/assignments/submissions/getSelf',
                key: '/coursesms/assignments/submissions/getSelf',
                urlParams: { assignmentId: '{assignmentId}' },
              },
              columns: [
                { id: 'name', dataField: 'name' },
                { id: 'email', dataField: 'email' },
                'createdDate',
              ],
              actions: [
                {
                  id: 'threeD_model',
                  onClick: (props: any) => {
                    props.navigate(`viewer/${props.row.id}`);
                  },
                  displayTooltip: true,
                  tooltipText: 'View 3D model',
                } as any,
              ],
            },
          },
        },
      },
    ],
  },
  {
    id: 'assignment_resources',
    title: 'Resources',
    components: [
      {
        Component: IndexContainer,
        props: {
          toolbar: {
            id: 'tlb_resources',
            cat: 'toolbar',
            type: 'index',
            left: [],
            right: [],
          },
          table: {
            cat: 'table',
            type: 'editor',
            id: 'tlb_resources',
            props: {
              source: {
                type: 'REMOTE',
                url: '/mediasms/mediaitemresources',
                key: '/mediasms/mediaitemresources',
                urlParams: {
                  subjectId: '{assignmentId}',
                  courseId: '{courseId}',
                },
              },
              columns: [
                { id: 'name' },
                { id: 'type' },
                { id: 'size' },
                { id: 'modifiedDate' },
                { id: 'fileExtension', text: 'Type' },
              ],
              actions: [
                {
                  id: 'download',
                  onClick: (props: any) =>
                    downloadFile(props.row.url, props.row.fileName),
                  displayTooltip: true,
                  tooltipText: 'Download resource',
                },
              ],
            },
          },
        },
      },
    ],
  },
  {
    id: 'assignment_building_config',
    title: 'Building Configurations',
    components: [
      {
        Component: IndexContainer,
        props: {
          toolbar: {
            id: 'tlb_building_config',
            cat: 'toolbar',
            type: 'index',
            left: [],
            right: [],
          },
          table: {
            cat: 'table',
            type: 'editor',
            id: 'tlb_building_config',
            props: {
              source: {
                type: 'REMOTE',
                url: '/buildingsconfigms/configurations',
                key: '/buildingsconfigms/configurations',
                urlParams: { subjectId: '{assignmentId}' },
              },
              columns: [
                { id: 'name' },
                { id: 'description' },
                { id: 'createdDate' },
                { id: 'modifiedDate' },
              ],
              actions: [
                {
                  id: 'review',
                  onClick: (props: any) => {
                    props.navigate(`/building-configurations/review/${props.row.id}`);
                  },
                  displayTooltip: true,
                  tooltipText: 'Review configuration',
                },
                {
                  id: 'view',
                  onClick: (props: any) => {
                    props.navigate(`specification/${props.row.id}`);
                  },
                  displayTooltip: true,
                  tooltipText: 'View specification',
                },
              ],
            },
          },
        },
      },
    ],
  },
];

/**
 * Configuration Object: assignmentsSection
 *
 * This object contains the configuration for the students assignments section.
 * It includes roles, layout, URL parameters, columns, and actions.
 */
export const assignmentsSection: ConfigSection = {
  id: 'assignments',
  path: '/assignments',
  roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
  layout: layoutWorkspace,
  screens: [
    {
      id: 'index',
      title: 'My assignments',
      path: '.',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      left: [],
      Component: PageTable,
      tmp: 'index',
      source: '/coursesms/assignments/self',
      endpoints: '/coursesms/assignments/self',
      empty: assignmentEntityConfig.empty,
      actions: [
        {
          id: 'view',
          onClick: (props: any) => {
            props.navigate(`${props.row.courseId}/${props.row.id}`);
          },
        },
      ],
      columns: [CourseColumn, GroupColumn],
    } as any,
  ],
  sections: [
    {
      id: 'assignment',
      tmp: 'tabbed',
      path: ':courseId/:assignmentId',
      layout: layoutWorkspace,
      Component: PageTabed,
      screens: [
        {
          id: 'assignment_tabs',
          title: 'Assignment Information',
          path: '',
          tabs: assignmentTabs,
        },
        {
          id: 'submission_distellar_view',
          path: 'viewer/:submissionId',
          roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
          tmp: 'custom',
          layout: layoutWorkspace,
          children: [
            {
              Component: DiStellarViewer,
              cat: 'page',
              type: 'index',
              layout: layoutWorkspace,
              props: {
                isInCourseAssignment: true,
              },
            },
          ],
        },
        {
          id: 'view_specification',
          title: 'View Specification',
          path: 'specification/:buildingConfigurationId',
          tmp: 'custom',
          Component: JsonViewer,
        },
      ],
    },
  ] as any,
};
